import React from "react";
import { currenySymbol, formatDate, isMobileApp, NO_IMAGE_FOUND, roundUpTo2 } from "../../../actions/Util";
import Icon from "./Icon";

class CustomTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedRow: null,
      showTopArrow: false,
      showBottomArrow: false
    };
    this.tableContainerRef = React.createRef();
    this.scrollPositionRef = 0;
    this.isLoadingRef = false;
    this.currencySymbol = null;
  }

  componentDidMount() {
    const { allowMobilePagination = false } = this.props;
    const mobileView = isMobileApp();

    console.log("columns", this.props.columns);
    // Set up scroll event listener
    const container = this.tableContainerRef.current;
    if (container && allowMobilePagination && mobileView) {
      container.addEventListener('scroll', this.checkScrollPosition);
      this.checkScrollPosition();
    }

    // Check scroll position when data changes
    this.checkScrollPositionDelayed();
  }

  componentDidUpdate(prevProps) {
    const { rows, pagination = {}, allowMobilePagination = false } = this.props;
    const { currentPage = 1, totalCount = 0, loading: paginationLoading = false } = pagination;
    const mobileView = isMobileApp();

    // Check scroll position when data changes
    if (allowMobilePagination && mobileView) {
      setTimeout(() => {
        this.checkScrollPosition();
      }, 100);
    }

    // Restore scroll position after data loads (only for arrow clicks)
    if (!paginationLoading && this.scrollPositionRef > 0) {
      const container = this.tableContainerRef.current;
      if (container) {
        setTimeout(() => {
          container.scrollTop = this.scrollPositionRef;
          this.scrollPositionRef = 0;
          setTimeout(() => this.checkScrollPosition(), 50);
        }, 0);
      }
    }

    // Reset loading flag when loading state changes
    if (!paginationLoading) {
      this.isLoadingRef = false;
    }
  }

  componentWillUnmount() {
    const { allowMobilePagination = false } = this.props;
    const mobileView = isMobileApp();
    const container = this.tableContainerRef.current;

    if (container && allowMobilePagination && mobileView) {
      container.removeEventListener('scroll', this.checkScrollPosition);
    }
  }

  checkScrollPosition = () => {
    const { allowMobilePagination = false, pagination = {} } = this.props;
    const { currentPage = 1, totalCount = 0, rowsPerPage = 25 } = pagination;
    const mobileView = isMobileApp();
    const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

    const container = this.tableContainerRef.current;
    if (!container || !allowMobilePagination || !mobileView) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const isAtTop = scrollTop <= 10;
    const isAtBottom = scrollHeight - scrollTop - clientHeight <= 10;

    this.setState({
      showTopArrow: isAtTop && currentPage > 1,
      showBottomArrow: isAtBottom && currentPage < totalPages
    });
  }

  checkScrollPositionDelayed = () => {
    setTimeout(() => {
      this.checkScrollPosition();
    }, 100);
  }

  handleArrowClick = (direction) => {
    const { pagination = {}, paginationLoading = false } = this.props;
    const { currentPage = 1, totalCount = 0, rowsPerPage = 25, onPageChange = () => { } } = pagination;
    const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

    if (paginationLoading || this.isLoadingRef) return;

    if (direction === 'top' && currentPage > 1) {
      this.isLoadingRef = true;
      console.log('Loading previous page via arrow:', currentPage - 1);

      const container = this.tableContainerRef.current;
      if (container) {
        this.scrollPositionRef = container.scrollTop;
      }

      onPageChange(currentPage - 1);

      setTimeout(() => {
        this.isLoadingRef = false;
      }, 1500);
    } else if (direction === 'bottom' && currentPage < totalPages) {
      this.isLoadingRef = true;
      console.log('Loading next page via arrow:', currentPage + 1);

      const container = this.tableContainerRef.current;
      if (container) {
        this.scrollPositionRef = container.scrollTop;
      }

      onPageChange(currentPage + 1);

      setTimeout(() => {
        this.isLoadingRef = false;
      }, 1500);
    }
  }

  getNestedValue = (obj, path) => {
    if (!obj || !path) return '';

    // Column names that contain a dot (e.g. a custom field named "Pkd. On") are flat keys, not nested paths
    if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path];

    const pathParts = path.split('.');
    let value = obj;

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      if (!value) break;

      const arrayMatch = part.match(/(\w+)\[(\d+)\]/);
      if (arrayMatch) {
        const arrayName = arrayMatch[1];
        const arrayIndex = parseInt(arrayMatch[2], 10);
        if (value[arrayName] && value[arrayName][arrayIndex]) {
          value = value[arrayName][arrayIndex];
        } else {
          value = null;
          break;
        }
      } else {
        value = value[part];
      }
    }

    return value || '';
  }

  calculateColumnTotals = () => {
    const { columns, rows } = this.props;
    const totals = {};

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      if (column.showTotal) {
        let sum = 0;
        if (rows && rows.length > 0) {
          for (let j = 0; j < rows.length; j++) {
            const row = rows[j];
            let value;

            if (column.renderCell) {
              // If there's a custom renderCell, try to get the raw value first
              // Check if the column has originalField or store the raw value
              if (column.rawField) {
                value = this.getNestedValue(row, column.rawField);
              } else if (column.field) {
                value = this.getNestedValue(row, column.field);
              } else {
                // Fallback: try to parse from the rendered output
                // This is less reliable but works for simple cases
                const renderedOutput = column.renderCell({ row, index: j });
                if (typeof renderedOutput === 'string') {
                  value = parseFloat(renderedOutput);
                } else if (renderedOutput && renderedOutput.props && renderedOutput.props.children) {
                  // Extract from JSX div children
                  const children = renderedOutput.props.children;
                  if (typeof children === 'string') {
                    value = parseFloat(children);
                  }
                }
              }
            } else {
              value = this.getNestedValue(row, column.field);
            }

            value = parseFloat(value) || 0;
            if (!isNaN(value)) {
              sum += value;
            }
          }
        }
        totals[column.field] = sum;
      }
    }
    return totals;
  }

  formatDateFromTimestamp = (value) => {
    if (!value) return "";
    const timestamp = value.toString().length === 10 ? value * 1000 : value;
    return new Date(timestamp).toISOString().split("T")[0];
  }

  renderShimmerLoader = (rows = 5, hasActions = false) => {
    const shimmerAnimation = {
      animation: 'shimmer-pulse 1.5s ease-in-out infinite'
    };

    const shimmerLineStyle = {
      height: '16px',
      background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
      backgroundSize: '200% 100%',
      borderRadius: '4px',
      animation: 'shimmer 1.5s ease-in-out infinite'
    };

    const shimmerCheckboxStyle = {
      width: '16px',
      height: '16px',
      background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
      backgroundSize: '200% 100%',
      borderRadius: '3px',
      animation: 'shimmer 1.5s ease-in-out infinite'
    };

    const shimmerButtonStyle = {
      width: '60px',
      height: '28px',
      background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
      backgroundSize: '200% 100%',
      borderRadius: '4px',
      margin: '0 auto',
      animation: 'shimmer 1.5s ease-in-out infinite'
    };

    return Array.from({ length: rows }).map((_, index) => (
      <tr key={`shimmer-${index}`} style={shimmerAnimation}>
        {this.props.columns.map((col, colIndex) => (
          <td key={`shimmer-cell-${colIndex}`} style={{ padding: "10px 12px" }}>
            {col.Type === "checkbox" ? (
              <div style={shimmerCheckboxStyle}></div>
            ) : (
              <div style={shimmerLineStyle}></div>
            )}
          </td>
        ))}
        {hasActions && (
          <td style={{ padding: "8px", textAlign: "center" }}>
            <div style={shimmerButtonStyle}></div>
          </td>
        )}
      </tr>
    ));
  };
  renderMobilePaginationArrows = () => {
    const { allowMobilePagination = false, pagination = {} } = this.props;
    const { totalCount = 0, rowsPerPage = 25, loading: paginationLoading = false } = pagination;
    const mobileView = isMobileApp();
    const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const { showTopArrow, showBottomArrow } = this.state;

    if (!allowMobilePagination || !mobileView || totalPages <= 1) return null;
    if (!showTopArrow && !showBottomArrow) return null;

    const arrowContainerStyle = {
      position: 'fixed',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 30
    };

    const buttonStyle = {
      backgroundColor: '#4f46e5',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '9999px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer',
      border: 'none'
    };

    const flexContainerStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '14px'
    };

    const iconStyle = {
      width: '16px',
      height: '16px'
    };

    const loadingStyle = {
      position: 'fixed',
      bottom: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 30,
      backgroundColor: '#4f46e5',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '9999px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    };

    const spinnerStyle = {
      width: '16px',
      height: '16px',
      border: '2px solid white',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    };

    return (
      <React.Fragment>
        {showTopArrow && !paginationLoading && (
          <div style={{ ...arrowContainerStyle, top: '16px' }} onClick={() => this.handleArrowClick('top')}>
            <div style={buttonStyle}>
              <div style={flexContainerStyle}>
                <div style={iconStyle}>
                  <Icon type="uparrow" />
                </div>
                Previous
              </div>
            </div>
          </div>
        )}

        {showBottomArrow && !paginationLoading && (
          <div style={{ ...arrowContainerStyle, bottom: '16px' }} onClick={() => this.handleArrowClick('bottom')}>
            <div style={buttonStyle}>
              <div style={flexContainerStyle}>
                <div style={iconStyle}>
                  <Icon type="downarrow" />
                </div>
                Next
              </div>
            </div>
          </div>
        )}

        {paginationLoading && (
          <div style={loadingStyle}>
            <div style={flexContainerStyle}>
              <div style={spinnerStyle}></div>
              Loading...
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  renderMobileView = () => {
    const { loading, loadingRows = 5, rows, showTextOnRows, columns, selectedRowId, handleEdit, handleDelete, handleView, handleOnRow, pagination = {} } = this.props;
    const { currentPage = 1, totalCount = 0, loading: paginationLoading = false } = pagination;
    const totalPages = Math.max(1, Math.ceil(totalCount / (pagination.rowsPerPage || 25)));
    const currencySymbol = this.currencySymbol;

    if (loading) {
      const shimmerCardStyle = {
        padding: '16px',
        borderBottom: '1px solid #e9d5ff',
        backgroundColor: '#faf5ff',
        height: '96px',
        animation: 'shimmer 1.5s ease-in-out infinite',
        background: 'linear-gradient(90deg, #faf5ff 0%, #f3e8ff 50%, #faf5ff 100%)',
        backgroundSize: '200% 100%'
      };

      return (
        <div>
          {Array.from({ length: loadingRows }).map((_, index) => (
            <div key={index} style={shimmerCardStyle}></div>
          ))}
        </div>
      );
    }

    if (showTextOnRows) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
          {showTextOnRows}
        </div>
      );
    }

    const cardContainerStyle = {
      spaceY: '12px',
      paddingBottom: '16px'
    };

    return (
      <div>
        {paginationLoading && currentPage > 1 && (
          <div style={{ padding: '12px', textAlign: 'center', backgroundColor: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#2563eb' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #2563eb',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Loading previous page...
            </div>
          </div>
        )}

        {rows && rows.map((row, index) => {
          const hasImageColumn = columns.some(col => col.type === "image" && !col.hideOnMobile);
          const rowKey = row.key || index;
          const isSelected = selectedRowId === (row.id || row.key || index);

          const rowStyle = {
            paddingTop: '8px',
            paddingBottom: '8px',
            backgroundColor: isSelected ? '#eef2ff' : 'white'
          };

          if (index !== rows.length - 1) {
            rowStyle.borderBottom = '1px solid #e9d5ff';
          }

          return (
            <div
              key={rowKey}
              style={rowStyle}
              onClick={(e) => {
                const clickedCell = e.target.closest("td");
                if (clickedCell) {
                  const parentNode = clickedCell.parentNode;
                  const children = Array.from(parentNode.children);
                  const columnIndex = children.indexOf(clickedCell);
                  const column = columns[columnIndex];

                  if (column && column.dontCallOnRowAction) {
                    return;
                  }
                }
                if (handleOnRow) {
                  handleOnRow(row);
                }
                if (handleEdit) {
                  handleEdit(row);
                }
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '8px', paddingRight: '8px' }}>
                {hasImageColumn && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ flexShrink: 0, width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      {columns.map((col, colIndex) => {
                        if (col.hideOnMobile || col.type !== "image") return null;
                        const cellValue = col.renderCell
                          ? col.renderCell({ row, index })
                          : this.getNestedValue(row, col.field) || "";
                        return (
                          <img
                            key={`image-${rowKey}`}
                            src={cellValue || NO_IMAGE_FOUND}
                            alt={cellValue || "Image"}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "4px"
                            }}
                          />
                        );
                      })}
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {columns.map((col, colIndex) => {
                        if (col.hideOnMobile || col.type === "image") return null;

                        const cellValue = col.renderCell
                          ? col.renderCell({ row, index })
                          : this.getNestedValue(row, col.field) || "";

                        let displayValue;
                        if (col.type === "boolean") {
                          displayValue = cellValue ? "Yes" : "No";
                        } else if (col.type === "currency") {
                          displayValue = `${currencySymbol} ${cellValue !== "" ? cellValue : "0"}`;
                        } else if (col.type === "percentage") {
                          displayValue = `${cellValue !== "" ? cellValue : "0"} %`;
                        } else {
                          displayValue = cellValue;
                        }

                        if (col.hideHeaderNameOnMobile) {
                          return (
                            <div key={`${colIndex}-${rowKey}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#000000' }}>{displayValue}</span>
                            </div>
                          );
                        }

                        return (
                          <div key={`${colIndex}-${rowKey}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#6b7280' }}>{col.headerName}:</span>
                            <span style={{ paddingLeft: '8px', color: '#000000' }}>{displayValue}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!hasImageColumn && (
                  <>
                    {columns.map((col, colIndex) => {
                      if (col.hideOnMobile) return null;

                      const cellValue = col.renderCell
                        ? col.renderCell({ row, index })
                        : this.getNestedValue(row, col.field) || "";

                      let displayValue;
                      if (col.type === "boolean") {
                        displayValue = cellValue ? "Yes" : "No";
                      } else if (col.type === "currency") {
                        displayValue = `${currencySymbol} ${cellValue !== "" ? cellValue : "0"}`;
                      } else if (col.type === "percentage") {
                        displayValue = `${cellValue !== "" ? cellValue : "0"} %`;
                      } else {
                        displayValue = cellValue;
                      }

                      if (col.hideHeaderNameOnMobile) {
                        return (
                          <div key={`${colIndex}-${rowKey}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#000000', width: '100%' }}>{displayValue}</span>
                            <span style={{ fontSize: '14px' }}>
                              {col.type === "image" && (
                                <div style={{ width: '40px', height: '40px', display: 'flex', overflow: 'hidden' }}>
                                  <img
                                    src={cellValue || NO_IMAGE_FOUND}
                                    alt={cellValue || "Image"}
                                    style={{ borderRadius: '8px', objectFit: 'cover' }}
                                  />
                                </div>
                              )}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div key={`${colIndex}-${rowKey}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>{col.headerName}:</span>
                          <span style={{ paddingLeft: '8px', color: '#000000' }}>{displayValue}</span>
                          <span style={{ fontSize: '14px' }}>
                            {col.type === "image" && (
                              <div style={{ width: '40px', height: '40px', display: 'flex', overflow: 'hidden' }}>
                                <img
                                  src={cellValue || NO_IMAGE_FOUND}
                                  alt={cellValue || "Image"}
                                  style={{ borderRadius: '8px', objectFit: 'cover' }}
                                />
                              </div>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {(handleEdit || handleDelete || handleView) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '12px', marginRight: '12px', paddingTop: '12px' }}>
                  {handleEdit && (!row.IsDeleted) && (
                    <span
                      style={{ width: '22px', color: '#6b7280', display: 'inline-block', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(row);
                      }}
                      label="edit"
                    >
                      <Icon type="edit" />
                    </span>
                  )}
                  {handleDelete && (!row.IsDeleted) && (
                    <span
                      style={{ width: '20px', display: 'inline-block', color: '#6b7280', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(row);
                      }}
                      label="delete"
                    >
                      <Icon type="delete" />
                    </span>
                  )}
                  {handleView && (
                    <span
                      style={{ width: '20px', display: 'inline-block', color: '#6b7280', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(row);
                      }}
                      label="View"
                    >
                      <Icon type="view" />
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {paginationLoading && currentPage < totalPages && (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#6b7280' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #4f46e5',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Loading more products...
            </div>
          </div>
        )}
      </div>
    );
  }

  renderDesktopView = () => {
    const { columns, rows, loading, loadingRows = 5, showTextOnRows, noHeader, noSticky, handleEdit, handleDelete, handleView, handleOnRow, selectedRowId } = this.props;
    const currencySymbol = this.currencySymbol;
    const columnTotals = this.calculateColumnTotals();

    const tableStyle = {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0
    };

    const thBaseStyle = {
      padding: '5px 10px',
      fontSize: '10px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      borderBottom: '1px solid #e5e7eb'
    };

    const tdBaseStyle = {
      padding: '8px 12px',
      verticalAlign: 'top'
    };

    return (
      <table style={tableStyle}>
        {!noHeader && (
          <thead style={{ position: noSticky ? 'static' : 'sticky', top: 0 }}>
            <tr className="bg-indigo-soft-gradient">
              {columns.map((col, colIndex) => {
                const thStyle = { ...thBaseStyle };
                if (col.align === "right") {
                  thStyle.textAlign = "right";
                } else {
                  thStyle.textAlign = "left";
                }
                if (col.type === "number" || col.type === "currency") {
                  thStyle.textAlign = "right";
                }
                if (col.width) {
                  thStyle.width = col.width;
                }
                return (
                  <th key={colIndex} style={thStyle}>
                    {col.headerName}
                  </th>
                );
              })}
              {(handleEdit || handleDelete || handleView) && (
                <th style={{ padding: '8px 4px', width: '160px', paddingTop: '8px', paddingBottom: '8px', fontWeight: 500, textAlign: 'center', color: '#4b5563', minWidth: '120px' }}>Actions</th>
              )}
            </tr>
          </thead>
        )}
        <tbody>
          {loading ? (
            this.renderShimmerLoader(loadingRows, !!(handleEdit || handleDelete || handleView))
          ) : showTextOnRows ? (
            <tr>
              <td colSpan={columns.length + ((handleEdit || handleDelete || handleView) ? 1 : 0)}
                style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                {showTextOnRows}
              </td>
            </tr>
          ) : (
            <React.Fragment>
              {rows && rows.map((row, index) => {
                const rowKey = row.key || index;
                const isSelected = selectedRowId === (row.id || row.key || index);
                const rowStyle = {
                  backgroundColor: isSelected ? '#eef2ff' : 'transparent'
                };

                const tdBorderStyle = index !== rows.length - 1 ? { borderBottom: '1px solid #e9d5ff' } : {};

                return (
                  <tr
                    key={rowKey}
                    style={{ ...rowStyle, cursor: (handleOnRow || handleEdit) ? 'pointer' : 'default' }}
                    onClick={(e) => {
                      const clickedCell = e.target.closest("td");
                      if (clickedCell) {
                        const parentNode = clickedCell.parentNode;
                        const children = Array.from(parentNode.children);
                        const columnIndex = children.indexOf(clickedCell);
                        const column = columns[columnIndex];

                        if (column && column.dontCallOnRowAction) {
                          return;
                        }
                      }
                      if (handleOnRow) {
                        handleOnRow(row);
                      } else if (handleEdit) {
                        const hasIsDeleted = Object.prototype.hasOwnProperty.call(row, 'IsDeleted');
                        if (hasIsDeleted) {
                          if (!row.IsDeleted) {
                            handleEdit(row);
                          }
                          if (row.IsDeleted) {
                            alert("selected item is deleted !");
                          }
                        } else {
                          handleEdit(row);
                        }
                      }
                    }}
                  >
                    {columns.map((col, colIndex) => {
                      const cellValue = col.renderCell
                        ? col.renderCell({ row, index })
                        : this.getNestedValue(row, col.field) || "";

                      const tdStyle = {
                        ...tdBaseStyle,
                        ...tdBorderStyle
                      };

                      if (col.align === "right") {
                        tdStyle.textAlign = "right";
                      } else {
                        tdStyle.textAlign = "left";
                      }

                      if (col.type === "number" || col.type === "currency" || col.type === "percentage") {
                        tdStyle.textAlign = "right";
                      }

                      if (col.type === "date" || col.type === "number" || col.type === "currency") {
                        tdStyle.whiteSpace = "nowrap";
                      }

                      return (
                        <td key={`${colIndex} ${rowKey}-${col.field}`} style={tdStyle}>
                          {col.type === "image" ? (
                            <div style={{ width: '100px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                              <img
                                src={cellValue || NO_IMAGE_FOUND}
                                alt={cellValue || "Image"}
                                style={{
                                  width: "100px",
                                  height: "100px",
                                  objectFit: "cover",
                                  borderRadius: "4px"
                                }}
                              />
                            </div>
                          ) : col.type === "currency" ? (
                            `${currencySymbol} ${cellValue !== "" ? roundUpTo2(cellValue) : "0"}`
                          ) : col.type === "date" ? (
                            cellValue ? formatDate(cellValue) : ""
                          ) : col.type === "percentage" ? (
                            `${cellValue !== "" ? cellValue : "0"} %`
                          ) : col.type === "boolean" ? (
                            `${cellValue ? "yes" : "No"}`
                          ) : (
                            cellValue !== "" ? cellValue : (col.type === "number" ? "0" : "-")
                          )}
                        </td>
                      );
                    })}
                    {(handleEdit || handleDelete || handleView) && (
                      <td style={{ padding: '8px 4px', width: '160px', paddingTop: '8px', paddingBottom: '8px', textAlign: 'center', verticalAlign: 'top', ...tdBorderStyle }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', alignItems: 'center' }}>
                          {handleEdit && (!row.IsDeleted) && (
                            <span
                              style={{ width: '22px', color: '#6b7280', display: 'inline-block', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                              }}
                              label="edit"
                            >
                              <Icon type="edit" />
                            </span>
                          )}
                          {handleDelete && (!row.IsDeleted) && (
                            <span
                              style={{ width: '20px', display: 'inline-block', color: '#6b7280', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row, index);
                              }}
                              label="delete"
                            >
                              <Icon type="delete" />
                            </span>
                          )}
                          {handleView && (
                            <span
                              style={{ width: '20px', display: 'inline-block', color: '#6b7280', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(row);
                              }}
                              label="View"
                            >
                              <Icon type="view" />
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {(Object.keys(columnTotals).length > 0 && rows && rows.length > 0) && (
                <tr style={{ backgroundColor: '#f9fafb', fontWeight: 500 }}>
                  {columns.map((col, colIndex) => {
                    const tdStyle = {
                      padding: '8px 12px',
                      borderTop: '1px solid #e9d5ff'
                    };

                    if (col.align === "right") {
                      tdStyle.textAlign = "right";
                    } else {
                      tdStyle.textAlign = "left";
                    }

                    if (col.type === "number" || col.type === "currency") {
                      tdStyle.textAlign = "right";
                      tdStyle.whiteSpace = "nowrap";
                    }

                    if (col.showTotal) {
                      return (
                        <td key={`total-${col.field}`} style={tdStyle}>
                          {col.type === "currency" ? (
                            `${currencySymbol} ${columnTotals[col.field].toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`
                          ) : (
                            columnTotals[col.field].toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={`total-${col.field}`} style={{ ...tdStyle, ...(colIndex === 0 ? {} : {}) }}>
                        {colIndex === 0 ? 'Total' : ''}
                      </td>
                    );
                  })}
                  {(handleEdit || handleDelete || handleView) && (
                    <td style={{ padding: '8px 4px', width: '160px', paddingTop: '8px', paddingBottom: '8px', textAlign: 'center' }}></td>
                  )}
                </tr>
              )}
            </React.Fragment>
          )}
        </tbody>
      </table>
    );
  }

  render() {
    const { noShadow = false, allowCompressInMobile = false, children } = this.props;
    const mobileView = isMobileApp();

    // Get currency symbol from hook (need to handle this carefully)
    // Since hooks can't be used in class components, we'll need to use a different approach
    // For now, we'll call the hook in render but this is not ideal
    // Better to pass currencySymbol as prop or use a HOC
    const CurrencySymbolComponent = () => {
      const symbol = currenySymbol();
      this.currencySymbol = symbol;
      return null;
    };

    const containerStyle = {
      backgroundColor: 'white',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: noShadow ? '1px solid #eeeeee' : '1px solid #eeeeee'
    };

    const innerContainerStyle = {
      width: '100%',
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    };

    const scrollContainerStyle = {
      position: 'relative',
      flex: 1,
      overflow: 'auto'
    };

    // Add animation keyframes to document head
    if (typeof document !== 'undefined' && !document.getElementById('table-spinner-styles')) {
      const styleSheet = document.createElement("style");
      styleSheet.id = 'table-spinner-styles';
      styleSheet.textContent = `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `;
      document.head.appendChild(styleSheet);
    }

    return (
      <React.Fragment>
        <CurrencySymbolComponent />
        <div style={containerStyle}>
          <div style={innerContainerStyle}>
            <div
              ref={this.tableContainerRef}
              style={scrollContainerStyle}
              onScroll={this.checkScrollPosition}
            >
              {(mobileView && allowCompressInMobile) ? this.renderMobileView() : this.renderDesktopView()}
            </div>
            {this.renderMobilePaginationArrows()}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default CustomTable;